import type { ClubResearchRow } from '../api/clubResearch';

export type ClubResearchMode = 'power' | 'offense' | 'defense';
export type ResearchView = 'players' | 'teams';
export type ClubSort = { key: string; order: 'asc' | 'desc' };

export function clubLeadersForScope(row: ClubResearchRow, combined: boolean) {
  return (combined ? row.top_players : row.top_players_in_competition ?? row.top_players) ?? [];
}

export const CLUB_SCORING_METRICS = [
  { key: 'Goal', field: 'goals', label: 'G', name: 'Goals', points: 8, group: 'scoring' },
  { key: 'ShotOnTarget', field: 'shots_on_target', label: 'SOT', name: 'Shots on Target', points: 3, group: 'scoring' },
  { key: 'Shot', field: 'shots', label: 'SH', name: 'Shots', points: 1, group: 'scoring' },
  { key: 'GCA', field: 'gca', label: 'GC', name: 'Goal Creation', points: 6, group: 'creating' },
  { key: 'SCA', field: 'sca', label: 'SC', name: 'Shot Creation', points: 1.5, group: 'creating' },
  { key: 'ProgPass', field: 'prog_passes', label: 'IP', name: 'Impact Passes', points: 1, group: 'progression' },
  { key: 'ProgCarry', field: 'prog_carries', label: 'IC', name: 'Impact Carries', points: 1, group: 'progression' },
  { key: 'Dispossessed', field: 'dispossessed', label: 'DIS', name: 'Dispossessed', points: -1, group: 'progression' },
] as const;

export const defaultClubSort = (mode: ClubResearchMode): ClubSort => ({
  key: mode === 'power' ? 'rating' : mode === 'offense' ? 'points_for_per_match' : 'points_against_per_match',
  order: mode === 'defense' ? 'asc' : 'desc',
});

/** Derive from exact totals, including during a staggered API/web rollout. */
export function clubPerGameValue(row: ClubResearchRow, key: string, side: 'for' | 'against' = 'for'): number | null {
  if (!row.games_played) return null;
  if (key === 'rating') return row.net_points / row.games_played;
  if (key === 'points_for_per_match') return row.points_for / row.games_played;
  if (key === 'points_against_per_match') return row.points_against / row.games_played;
  const count = row[`stats_${side}`][key];
  return count == null ? null : count / row.games_played;
}

/** Same palette/thirds as OPRK, but good club ranks are green (low is best). */
export function clubRankColorClass(rank: number | null, scopeSize: number): string {
  if (rank == null || scopeSize <= 0) return 'text-gray-400';
  if (rank <= Math.ceil(scopeSize / 3)) return 'text-green-400';
  if (rank >= Math.floor(2 * scopeSize / 3) + 1) return 'text-secondary';
  return 'text-yellow-400';
}

/** Instant competition filtering, with per-game ranks recomputed for that scope. */
export function rankClubScope(rows: readonly ClubResearchRow[]): ClubResearchRow[] {
  const ranked = rows.map(row => ({ ...row, stat_ranks_for: { ...row.stat_ranks_for }, stat_ranks_against: { ...row.stat_ranks_against } }));
  const ranks = (value: (row: ClubResearchRow) => number, descending: boolean) => {
    const eligible = ranked.filter(row => row.games_played > 0).sort((a, b) => (value(a) - value(b)) * (descending ? -1 : 1));
    const result = new Map<ClubResearchRow, number>();
    let previous: number | undefined, rank = 0;
    eligible.forEach((row, index) => {
      const current = value(row);
      if (previous === undefined || current !== previous) rank = index + 1;
      result.set(row, rank); previous = current;
    });
    return result;
  };
  for (const [field, rankField, descending] of [
    ['rating', 'power_rank', true], ['points_for_per_match', 'offense_rank', true], ['points_against_per_match', 'defense_rank', false],
  ] as const) {
    const result = ranks(row => clubPerGameValue(row, field)!, descending);
    ranked.forEach(row => { row[rankField] = result.get(row) ?? null; });
  }
  for (const side of ['for', 'against'] as const) {
    for (const metric of CLUB_SCORING_METRICS) {
      const result = ranks(row => clubPerGameValue(row, metric.key, side)!, (side === 'for') !== (metric.points < 0));
      ranked.forEach(row => { row[`stat_ranks_${side}`][metric.key] = result.get(row) ?? null; });
    }
  }
  return ranked;
}

export function nextClubSort(previous: ClubSort, key: string, mode: ClubResearchMode): ClubSort {
  if (previous.key === key) return { key, order: previous.order === 'asc' ? 'desc' : 'asc' };
  const rank = key === 'rank';
  const dispossessed = key === 'Dispossessed';
  const ascending = rank || key === 'club_name' || key === 'points_against_per_match'
    || (mode === 'defense' && !dispossessed)
    || (mode === 'offense' && dispossessed);
  return { key, order: ascending ? 'asc' : 'desc' };
}

export function sortClubRows(rows: readonly ClubResearchRow[], mode: ClubResearchMode, sort: ClubSort): ClubResearchRow[] {
  const rankField = mode === 'power' ? 'power_rank' : mode === 'offense' ? 'offense_rank' : 'defense_rank';
  const value = (row: ClubResearchRow): number | string | null => {
    if (sort.key === 'club_name') return row.club_name;
    if (row.games_played === 0) return null;
    if (sort.key === 'rank') return row[rankField];
    if (CLUB_SCORING_METRICS.some(metric => metric.key === sort.key)) {
      return clubPerGameValue(row, sort.key, mode === 'offense' ? 'for' : 'against');
    }
    return clubPerGameValue(row, sort.key);
  };
  return [...rows].sort((a, b) => {
    const av = value(a), bv = value(b);
    if (av == null) return bv == null ? a.club_name.localeCompare(b.club_name) : 1;
    if (bv == null) return -1;
    const delta = typeof av === 'string' && typeof bv === 'string' ? av.localeCompare(bv) : Number(av) - Number(bv);
    return delta * (sort.order === 'asc' ? 1 : -1) || a.club_name.localeCompare(b.club_name);
  });
}

export function seasonScoreLines(counts: Record<string, number>, actualPoints?: Record<string, number>) {
  return CLUB_SCORING_METRICS.map(metric => ({
    ...metric, count: counts[metric.key] ?? 0,
    total: actualPoints?.[metric.key] ?? (counts[metric.key] ?? 0) * metric.points,
  })).filter(metric => metric.count > 0).sort((a, b) => b.total - a.total);
}

export function supportedResearchCompetitions<T extends { name: string; short_name: string }>(competitions: readonly T[]): T[] {
  return competitions.filter(c => /premier league|la liga/i.test(c.name))
    .sort((a, b) => Number(/la liga/i.test(a.name)) - Number(/la liga/i.test(b.name)));
}
